import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';

export interface TxSetupResult {
  units: number;
  noPerDay: number;
  days: number;
  total: number;
  productExpDate: Date;
  refillCount: number;
  isPRN: boolean;
  refillExpireDate: Date;
  nextRefillDate: Date;
  reason:string;
  totalUnits?: number;
  instructions?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TxSetupService {

  async ShowRxSetupModal(initialUnits: number, unitLabel: string): Promise<TxSetupResult | null> {
    const oneYearOut = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];

    const { value: form } = await Swal.fire({
      title: 'Rx Label Setup',
      customClass: {
        popup: 'swal-wide-popup',
        htmlContainer: 'swal-html-container'
      },
      width: '600px',
      heightAuto: true,
      scrollbarPadding: false,
      html: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 10px; background: #ffffff; border-radius: 6px; max-width: 550px; color: #2c3e50; border: 1px solid #e0e0e0;">
  
  <style>
    .rx-main-grid {
      display: grid;
      grid-template-columns: 1fr 1fr; /* Dos columnas principales */
      gap: 8px 20px;
    }
    .rx-control-group {
      display: grid;
      grid-template-columns: auto 1fr; /* Label a la izquierda, Input a la derecha */
      align-items: center;
      gap: 8px;
    }
    .rx-label { 
      font-weight: 600; 
      font-size: 11px; 
      color: #546e7a; 
      white-space: nowrap; /* Evita que el texto salte de línea */
    }
    .rx-input { 
      padding: 3px 6px; 
      font-size: 12px; 
      border: 1px solid #cfd8dc; 
      border-radius: 3px; 
      box-sizing: border-box; 
      font-family: inherit;
      width: 100%;
      background: #fafafa;
    }
    .rx-input:focus {
      border-color: #3498db;
      outline: none;
      background: #fff;
    }
    .rx-divider { 
      grid-column: span 2;
      border: none; 
      border-top: 1px solid #eee; 
      margin: 2px 0; 
    }
  </style>

  <div class="rx-main-grid">
    <div class="rx-control-group">
      <label class="rx-label">Give each time:</label>
      <input id="rx-units" class="rx-input" type="number" value="${initialUnits}">
    </div>

    <div class="rx-control-group">
      <label class="rx-label">No. of times/day:</label>
      <input id="rx-noperday" class="rx-input" type="number" value="1">
    </div>

    <div class="rx-control-group">
      <label class="rx-label">Repeat In (days):</label>
      <input id="rx-days" class="rx-input" type="number" value="1">
    </div>

    <div class="rx-control-group">
      <label class="rx-label">Total Days:</label>
      <input id="rx-total" class="rx-input" type="number" value="1">
    </div>

    <div class="rx-control-group">
      <label class="rx-label">Product Exp. Date:</label>
      <input id="rx-expdate" class="rx-input" type="date" value="${today}">
    </div>

    <div class="rx-control-group">
      <div style="background: #edf7ed; padding: 3px 8px; border-radius: 3px; border: 1px solid #c3e6cb; display: flex; justify-content: space-between; width: 100%; box-sizing: border-box;">
        <span style="font-size: 10px; font-weight: 700; color: #1e4620;">Total Dispensed:</span>
        <span id="rx-total-dispensed" style="font-size: 11px; font-weight: 700; color: #2e7d32;">0 ${unitLabel}</span>
      </div>
    </div>

    <hr class="rx-divider">

    <div class="rx-control-group">
      <label class="rx-label">No. of refills:</label>
      <div style="display: flex; gap: 5px; align-items: center;">
        <input id="rx-refillcount" class="rx-input" type="text" value="0">
      </div>
    </div>

    <div class="rx-control-group">
      <label style="display: flex; align-items: center; gap: 2px; font-size: 10px; cursor: pointer;">
          <input id="rx-prn-check" type="checkbox" style="margin: 0;"> PRN
        </label>
    </div>

    <div class="rx-control-group">
      <label class="rx-label">Rx Expires On:</label>
      <input id="rx-refillexpire" class="rx-input" type="date" value="${oneYearOut}">
    </div>

    <div class="rx-control-group">
      <label class="rx-label">Next Refill On:</label>
      <input id="rx-nextrefill" class="rx-input" type="date" value="${tomorrow}">
    </div>
  </div>

</div>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      didOpen: (modal) => {
        const prnCheck = modal.querySelector('#rx-prn-check') as HTMLInputElement;
        const refillSection = modal.querySelector('#rx-refill-section') as HTMLElement;
        const unitsInput = modal.querySelector('#rx-units') as HTMLInputElement;
        const noPerDayInput = modal.querySelector('#rx-noperday') as HTMLInputElement;
        const daysInput = modal.querySelector('#rx-days') as HTMLInputElement;
        const totalInput = modal.querySelector('#rx-total') as HTMLInputElement;
        const totalDispensedSpan = modal.querySelector('#rx-total-dispensed') as HTMLElement;

        const updateTotalDispensed = () => {
          const units = parseFloat(unitsInput.value) || 0;
          const noPerDay = parseFloat(noPerDayInput.value) || 0;
          const days = parseFloat(daysInput.value) || 1;
          const total = parseFloat(totalInput.value) || 1;

          let intTemp = Math.floor(total / days);
          if (total % days > 0) intTemp++;

          const totalDispensed = units * noPerDay * intTemp;
          totalDispensedSpan.textContent = `${totalDispensed.toFixed(2)} ${unitLabel}`;
        };

        prnCheck?.addEventListener('change', () => {
          if (refillSection) {
            refillSection.style.display = prnCheck.checked ? 'block' : 'none';
          }
        });

        unitsInput?.addEventListener('change', updateTotalDispensed);
        noPerDayInput?.addEventListener('change', updateTotalDispensed);
        daysInput?.addEventListener('change', updateTotalDispensed);
        totalInput?.addEventListener('change', updateTotalDispensed);
      },
      preConfirm: () => {
        const units = parseFloat((document.getElementById('rx-units') as HTMLInputElement).value);
        const noPerDay = parseFloat((document.getElementById('rx-noperday') as HTMLInputElement).value);
        const days = parseInt((document.getElementById('rx-days') as HTMLInputElement).value);
        const total = parseInt((document.getElementById('rx-total') as HTMLInputElement).value);
        const productExpDate = (document.getElementById('rx-expdate') as HTMLInputElement).value;
        const prnCheck = (document.getElementById('rx-prn-check') as HTMLInputElement).checked;

        if (isNaN(units) || units <= 0) {
          Swal.showValidationMessage('Units must be a positive number');
          return false;
        }
        if (isNaN(noPerDay) || noPerDay < 0) {
          Swal.showValidationMessage('No. of times per day must be a positive number');
          return false;
        }
        if (isNaN(days) || days < 1) {
          Swal.showValidationMessage('Repeat in must be at least 1 day');
          return false;
        }
        if (isNaN(total) || total < 1) {
          Swal.showValidationMessage('Total days must be at least 1');
          return false;
        }

        let refillCount = 0;
        let refillExpireDate = oneYearOut;
        let nextRefillDate = tomorrow;
        let isPRN = false;

        if (prnCheck) {
          const refillCountInput = (document.getElementById('rx-refillcount') as HTMLInputElement).value.trim();
          refillExpireDate = (document.getElementById('rx-refillexpire') as HTMLInputElement).value;
          nextRefillDate = (document.getElementById('rx-nextrefill') as HTMLInputElement).value;

          if (!refillCountInput) {
            Swal.showValidationMessage('No. of refills is required when PRN is enabled');
            return false;
          }

          if (refillCountInput.toUpperCase() === 'PRN') {
            refillCount = -1;
            isPRN = true;
          } else {
            const count = parseInt(refillCountInput, 10);
            if (isNaN(count) || count < 0) {
              refillCount = 0;
            } else if (count <= 1) {
              refillCount = 0;
            } else {
              refillCount = count - 1;
            }
          }
        }

        return {
          units,
          noPerDay,
          days,
          total,
          productExpDate: new Date(productExpDate),
          refillCount,
          isPRN,
          refillExpireDate: new Date(refillExpireDate),
          nextRefillDate: new Date(nextRefillDate)
        };
      }
    });

    if (!form) return null;

    return {
      units: form.units,
      noPerDay: form.noPerDay,
      days: form.days,
      total: form.total,
      productExpDate: form.productExpDate,
      refillCount: form.refillCount,
      isPRN: form.isPRN,
      refillExpireDate: form.refillExpireDate,
      nextRefillDate: form.nextRefillDate,
      reason: form.reason ?? ''
    };
  }

  /**
   * Replace placeholder in label string
   * ~U = units per dose
   * ~T = times per day
   * ~D = days
   * ~N = total number of times
   */
  replaceLabelPlaceholders(
    label: string,
    units: number,
    noPerDay: number,
    days: number,
    total: number
  ): string {
    let result = label;

    // Replace ~U with units
    if (result.includes('~U')) {
      result = result.replace('~U', units.toString());
    }

    // Replace ~T with times per day
    if (result.includes('~T')) {
      result = result.replace('~T', noPerDay.toString());
    }

    // Replace ~D with days
    if (result.includes('~D')) {
      result = result.replace('~D', days.toString());
    }

    // Replace ~N with total number of times
    if (result.includes('~N')) {
      result = result.replace('~N', total.toString());
    }

    return result;
  }

  /**
   * Calculate total units to dispense
   */
  calculateTotalDispense(units: number, noPerDay: number, days: number, total: number): number {
    let intTemp = Math.floor(total / days);
    if (total % days > 0) intTemp++;
    return units * noPerDay * intTemp;
  }
}
